import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Onboarding, OnboardingProvider, OnboardingCustomFieldsProvider } from '@hub-mono/app-modules';
import { saveOnboarding, saveWelcomeData, updateNotificationsRequest, updateHubspotContact } from 'api/account';
import { validateSubDomainFree } from 'api/company';
import { updateCustomFields, deleteCustomFieldsOnboarding } from 'api/customField';
import PropTypes from 'prop-types';

import { updateResourceSubtitleId, updateOnboarded } from 'actions/companyActions';
import { getGeoLocation } from 'actions/generalActions';
import Cookies from 'js-cookie';
import { getAccount, selectGeoLocation } from 'selectors/account';
import { PageContainer } from '../../shared/styles';
import { useHistory } from 'react-router-dom';
import { company } from '../../../../actions/companyActions';
import { getAccount as getAccountAction } from '../../../../actions/accountActions';

const OnboardingComponent = () => {
    const dispatch = useDispatch();
    const geoLocation = useSelector(selectGeoLocation);
    const companySubDomain = useSelector(state => state.companyReducer.company.subdomain);
    const account = useSelector(getAccount);
    const updateResourceSubtitle = id => dispatch(updateResourceSubtitleId(id));
    const updateIsOnboarded = isOnboarded => dispatch(updateOnboarded(isOnboarded));
    const history = useHistory();

    const saveCustomFields = type => customFields => updateCustomFields(customFields, type, true, true);
    const deleteCustomFields = type => deleteCustomFieldsOnboarding(type);
    const onStepCallback = async data => await updateHubspotContact(data);

    const validateDomain = async domain => {
        if (domain === companySubDomain) return true;
        const { available } = await validateSubDomainFree(domain);
        return available;
    };

    useEffect(() => {
        dispatch(getGeoLocation.request());
    }, [dispatch]);

    const handleSubmit = async data => {
        try {
            const noDummyData = data.resourcesAmount === undefined && data.projectsAmount === undefined;
            const finalRedirectPath = noDummyData
                ? `startTour?welcome-to-hubplanner-3213444`
                : `resource/group/${account.preferences.myManagedResourcesGroupId}`;

            await saveWelcomeData({
                email: account.email,
                firstName: data.firstName,
                lastName: data.lastName,
                companyName: data.companyName,
                newFeatureEmailAgreed: data.newFeatureEmailAgreed,
                subdomain: data.subDomain,
                pageTitle: 'Hub Planner | Welcome',
                pageUrl: window.location.href,
                utk: Cookies.get('hubspotutk'),
                phoneNumber: data.phoneNumber || '',
            });

            const {
                settings: { grid },
            } = await saveOnboarding(data);

            dispatch(company.request());
            dispatch(getAccountAction.request());

            const notification = {
                category: 'MARKETING_EMAIL_NEW_FEATURES_NOTIFICATION',
                channel: 'EMAIL',
                enabled: data.newFeatureEmailAgreed,
                modifiedDate: new Date(),
            };

            const notifications = [...account.preferences.notifications, notification];

            await Promise.all([
                updateResourceSubtitleId(grid.resourceSubtitleRefId),
                updateNotificationsRequest(account._id, { notifications }),
                updateIsOnboarded(true),
            ]);

            return new Promise(resolve => {
                resolve(history.replace(finalRedirectPath));
            });
        } catch (error) {
            console.error('Error saving welcome data', error);
            throw error;
        }
    };

    return (
        <PageContainer>
            <OnboardingCustomFieldsProvider>
                <OnboardingProvider
                    submitOnboarding={handleSubmit}
                    deleteCustomFields={deleteCustomFields}
                    defaultCountry={{
                        name: geoLocation?.country_name,
                        code: geoLocation?.country_code,
                    }}
                    domain={companySubDomain}
                    saveCustomFields={saveCustomFields}
                    validateDomain={validateDomain}
                    updateResourceSubtitleId={updateResourceSubtitle}
                    onStepCallback={onStepCallback}
                >
                    <Onboarding />
                </OnboardingProvider>
            </OnboardingCustomFieldsProvider>
        </PageContainer>
    );
};

OnboardingComponent.propTypes = {
    account: PropTypes.shape({
        email: PropTypes.string.isRequired,
        _id: PropTypes.string.isRequired,
        preferences: PropTypes.shape({
            notifications: PropTypes.array.isRequired,
        }).isRequired,
    }).isRequired,
    subDomain: PropTypes.string,
    onSuccess: PropTypes.func,
    saveCustomFields: PropTypes.func.isRequired,
    validateDomain: PropTypes.func.isRequired,
    updateResourceSubtitleId: PropTypes.func.isRequired,
};

export default OnboardingComponent;
